// Data Sources page for Microsoft Foundry: one tab per connected Entra tenant, and the Log
// Analytics workspaces each connection reads.
import { useCallback, useEffect, useRef, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    addMicrosoftFoundryWorkspaces,
    createMicrosoftFoundryIntegration,
    deleteMicrosoftFoundryIntegration,
    getMicrosoftFoundryAuthUrl,
    removeMicrosoftFoundryWorkspace,
    replaceMicrosoftFoundrySecret,
} from "../../lib/microsoftFoundryClient.js";
import { directoryOauthRedirectUri } from "../../lib/oauthCallback.js";
import { useDirectoryOauthPopup } from "../../lib/useDirectoryOauthPopup.js";
import { MICROSOFT_CONNECT_METHODS } from "../../components/identity/microsoftConnectMethods.js";
import { MANAGED_MICROSOFT_PERMISSION_GROUPS } from "../../components/identity/microsoftPermissions.js";
import {
    FOUNDRY_GATEWAY_LOGS_MISSING,
    FOUNDRY_NOT_OFFERED,
    NO_DIRECTORY,
    consentedThroughOriginsApp,
    directoryMissing,
    directoryScoped,
    foundryAppName,
    foundryConnectionName,
    foundryMissingRole,
    foundryNeedsAttention,
    foundryProblem,
    foundrySteps,
    whenLabel,
} from "./model.js";
import {
    AsideFold,
    AsideSection,
    ConsentWait,
    CopyBlock,
    CopyValue,
    ErrorText,
    Field,
    Instructions,
    MethodOption,
    Panel,
    PermissionList,
    SourceLoadState,
    SourcePage,
    Step,
    Steps,
    Tabs,
    inputCls,
    linkCls,
} from "../../components/setupSteps.jsx";
import { TONE, dangerBtnCls, primaryBtnCls, secondaryBtnCls } from "../../components/statusCard.jsx";

const NEW_TAB = "new";
const CONSENT_PROVIDER = "MICROSOFT_FOUNDRY";
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
const WORKSPACE_ID =
    /^\/subscriptions\/[0-9a-f-]{36}\/resourcegroups\/[^/]+\/providers\/microsoft\.operationalinsights\/workspaces\/[a-z0-9-]{4,63}\/?$/i;
const MAX_WORKSPACES = 20;
const ROLE = "Privileged Monitoring Data Reader";
const CLIENT_ID = "CLIENT_ID";
const WORKSPACE_RESOURCE_ID = "WORKSPACE_RESOURCE_ID";
const APIM_RESOURCE_ID =
    "/subscriptions/SUBSCRIPTION_ID/resourceGroups/RESOURCE_GROUP/providers/Microsoft.ApiManagement/service/APIM_NAME";
const APP_INSIGHTS_RESOURCE_ID = "APPLICATION_INSIGHTS_RESOURCE_ID";
const POPUP_BLOCKED = "Your browser blocked the consent tab. Allow popups for this site, then try again.";
const MANAGED_SIGN_IN_UNAVAILABLE = "Not available in this Origin environment. Use your own app registration instead.";

const TABLE_LABEL = {
    ApiManagementGatewayLlmLog: "Gateway calls",
    AppGenAIContent: "Agent traces",
};

const WORKSPACE_STATUS = {
    ok: "Reading",
    no_data_yet: "No data yet",
    error: "Failing",
};

export const CONNECT_METHOD = {
    MANAGED: "managed",
    BYO: "byo",
};

// The last segment of an ARM resource ID: a workspace's or a model account's name.
function resourceName(resourceId) {
    return resourceId.split("/").filter(Boolean).at(-1) ?? resourceId;
}

function idList(text) {
    return [...new Set(text.split(/[\s,]+/).map((id) => id.trim().replace(/\/$/, "")).filter(Boolean))];
}

function roleCommand(clientId, workspaceIds) {
    const scopes = workspaceIds.length > 0 ? workspaceIds : [WORKSPACE_RESOURCE_ID];
    return scopes
        .map(
            (scope) =>
                `az role assignment create --assignee ${clientId || CLIENT_ID} --role "${ROLE}" --scope ${scope}`,
        )
        .join("\n");
}

// The API diagnostic update needs If-Match and the diagnostic's loggerId, and carries
// largeLanguageModel only from 2025-09-01-preview, so the logger is read first.
function gatewayCommands(workspaceId) {
    const workspace = workspaceId || WORKSPACE_RESOURCE_ID;
    const body = {
        properties: {
            loggerId: "$logger",
            sampling: { samplingType: "fixed", percentage: 100 },
            largeLanguageModel: {
                logs: "enabled",
                requests: { messages: "all", maxSizeInBytes: 262144 },
                responses: { messages: "all", maxSizeInBytes: 262144 },
            },
        },
    };
    return [
        `az monitor diagnostic-settings create --name origin --resource ${APIM_RESOURCE_ID} --workspace ${workspace} --export-to-resource-specific true --logs '[{"category":"GatewayLogs","enabled":true},{"category":"GatewayLlmLogs","enabled":true}]'`,
        `diag="https://management.azure.com${APIM_RESOURCE_ID}/apis/API_ID/diagnostics/azuremonitor?api-version=2025-09-01-preview"`,
        `logger=$(az rest --method get --url "$diag" --query properties.loggerId --output tsv)`,
        "cat > diagnostic.json <<EOF",
        JSON.stringify(body),
        "EOF",
        `az rest --method patch --url "$diag" --headers "If-Match=*" --body @diagnostic.json`,
        `az rest --method get --url "$diag" --query "properties.{sampling: sampling, llm: largeLanguageModel}"`,
    ].join("\n");
}

function connectionTone(integration) {
    if (foundryNeedsAttention(integration)) return TONE.ATTENTION;
    const states = foundrySteps(integration);
    return states.at(-1) === "done" ? TONE.OK : TONE.WAITING;
}

function Mono({ children }) {
    return <span className="font-mono text-[11.5px]">{children}</span>;
}

function Ui({ children }) {
    return <span className="font-medium text-[color:var(--ink)]">{children}</span>;
}

function Note({ children }) {
    return <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--muted)]">{children}</p>;
}

function GuideToggle({ open, onToggle, label = "steps", text = null }) {
    return (
        <button type="button" onClick={onToggle} aria-expanded={open} className={secondaryBtnCls}>
            {text ?? (open ? `Hide ${label}` : `Show ${label}`)}
        </button>
    );
}

function Fold({ summary, children }) {
    return (
        <details className="border border-[color:var(--line)] bg-[color:var(--panel)] px-[14px] py-[10px]">
            <summary className="cursor-pointer text-[12px] font-medium text-[color:var(--ink)]">{summary}</summary>
            <div className="mt-[10px] grid gap-[10px]">{children}</div>
        </details>
    );
}

// Commands for an admin who prefers a terminal; the portal steps stay the main instructions.
function TerminalCommands({ children }) {
    return <Fold summary="Use a terminal instead">{children}</Fold>;
}

const MANAGED_PERMISSION_COUNT = MANAGED_MICROSOFT_PERMISSION_GROUPS.reduce(
    (count, group) => count + group.permissions.length,
    0,
);

function ManagedPermissionList() {
    return (
        <PermissionList
            title={`View the ${MANAGED_PERMISSION_COUNT} read-only permissions Origin's app comes with. Directory sync and Copilot capture use them; Foundry uses none.`}
            groups={MANAGED_MICROSOFT_PERMISSION_GROUPS}
            note="They come with the consent even if you connect only Foundry. To grant nothing beyond the workspace role, use your own app registration instead."
        />
    );
}

// Managed consent is two Microsoft screens: an admin sign-in, then the approval. The Privileged
// Identity Management sentence is inferred, not tested.
function ConsentScreens() {
    return (
        <Note>
            Microsoft asks a Global Administrator or Privileged Role Administrator to sign in, then to approve Origin's
            app. If you hold the role through Privileged Identity Management, activate it first.
        </Note>
    );
}

const CONSENT_WAIT = "Finish in the Microsoft tab: sign in, then approve.";

function ManagedConnectForm({ onCreated }) {
    const caps = useMyPermissions();
    const [tenant, setTenant] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const pendingIdRef = useRef(null);
    const onCreatedRef = useRef(onCreated);
    onCreatedRef.current = onCreated;
    const typed = tenant.trim();
    const tenantInvalid = typed.length > 0 && !GUID.test(typed) && !DOMAIN.test(typed);

    const discardPending = useCallback(() => {
        const id = pendingIdRef.current;
        pendingIdRef.current = null;
        if (id) deleteMicrosoftFoundryIntegration(id).catch(() => {});
    }, []);

    const { pendingId, start, cancel } = useDirectoryOauthPopup({
        onResult: ({ status, message }) => {
            if (status === "success") {
                pendingIdRef.current = null;
                onCreatedRef.current();
                return;
            }
            discardPending();
            setError(message || "Microsoft did not complete admin consent.");
        },
        onClosedWithoutResult: () => {
            pendingIdRef.current = null;
            onCreatedRef.current();
        },
    });

    useEffect(() => discardPending, [discardPending]);

    const connect = useCallback(async () => {
        setSaving(true);
        setError(null);
        let newId = null;
        try {
            const created = await createMicrosoftFoundryIntegration({ managed: true, entraTenantId: typed });
            newId = created?.integration?.id ?? null;
            if (!newId) throw new Error("The connection was created but no ID came back. Try again.");
            if (created.integration.status === "connected") {
                newId = null;
                await onCreated();
                return;
            }
            const redirectUri = directoryOauthRedirectUri(window.location.origin);
            const { authUrl } = await getMicrosoftFoundryAuthUrl({ integrationId: newId, redirectUri });
            if (!start({ integrationId: newId, provider: CONSENT_PROVIDER, redirectUri, authUrl })) {
                await deleteMicrosoftFoundryIntegration(newId).catch(() => {});
                setError(POPUP_BLOCKED);
                return;
            }
            pendingIdRef.current = newId;
        } catch (err) {
            if (newId) await deleteMicrosoftFoundryIntegration(newId).catch(() => {});
            cancel();
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, cancel, onCreated, start, typed]);

    const stop = useCallback(() => {
        cancel();
        discardPending();
    }, [cancel, discardPending]);

    return (
        <div className="grid gap-[12px]">
            <ManagedPermissionList />
            <Field
                label="Microsoft tenant (optional)"
                error={tenantInvalid ? "Enter the tenant ID, or a domain such as contoso.onmicrosoft.com." : null}
                hint="Your Directory (tenant) ID or a domain such as contoso.onmicrosoft.com. If you already connected Entra directory sync or Microsoft 365 Copilot for this tenant through Origin's app, Foundry connects without another consent."
            >
                <input
                    type="text"
                    value={tenant}
                    spellCheck={false}
                    onChange={(event) => setTenant(event.target.value)}
                    placeholder="contoso.onmicrosoft.com"
                    className={`${inputCls} font-mono`}
                />
            </Field>
            <ConsentScreens />
            {pendingId ? (
                <ConsentWait onCancel={stop} message={CONSENT_WAIT} />
            ) : (
                <div>
                    <button type="button" onClick={connect} disabled={saving || tenantInvalid} className={primaryBtnCls}>
                        {saving ? "Opening Microsoft..." : "Continue to Microsoft"}
                    </button>
                </div>
            )}
            <ErrorText>{error}</ErrorText>
        </div>
    );
}

function ClientIdField({ value, onChange, hint }) {
    return (
        <Field label="Application (client) ID" hint={hint}>
            <input
                type="text"
                value={value}
                spellCheck={false}
                onChange={(event) => onChange(event.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className={`${inputCls} font-mono`}
            />
        </Field>
    );
}

function ClientSecretField({ value, onChange }) {
    return (
        <Field label="Client secret" hint="The secret's Value, not its ID. Stored encrypted and never shown again.">
            <input
                type="password"
                value={value}
                autoComplete="off"
                onChange={(event) => onChange(event.target.value)}
                className={inputCls}
            />
        </Field>
    );
}

function ByoConnectForm({ onCreated, onClientId }) {
    const caps = useMyPermissions();
    const [tenantId, setTenantId] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const tenantValid = GUID.test(tenantId.trim()) || DOMAIN.test(tenantId.trim());
    const clientValid = GUID.test(clientId.trim());
    const canConnect = tenantValid && clientValid && clientSecret.trim().length > 0 && !saving;

    const connect = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const created = await createMicrosoftFoundryIntegration({
                entraTenantId: tenantId.trim(),
                clientId: clientId.trim(),
                clientSecret: clientSecret.trim(),
            });
            const id = created?.integration?.id;
            if (id) onClientId(id, clientId.trim());
            setClientSecret("");
            await onCreated();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, clientId, clientSecret, onClientId, onCreated, tenantId]);

    return (
        <div className="grid gap-[12px]">
            <Note>
                The app needs no API permissions. Origin signs in as it and reads only the workspaces you grant it a
                role on, in the next step.
            </Note>
            <Fold summary="Don't have one?">
                <Instructions>
                    <li>
                        Entra admin center &gt; <Ui>App registrations</Ui> &gt; <Ui>New registration</Ui>: any name, single
                        tenant, no redirect URI.
                    </li>
                    <li>
                        <Ui>Certificates &amp; secrets</Ui> &gt; <Ui>New client secret</Ui>. Copy its <Ui>Value</Ui> and
                        note when it expires; capture stops when it does.
                    </li>
                    <li>
                        Paste the IDs from the app's <Ui>Overview</Ui> page and the secret below.
                    </li>
                </Instructions>
            </Fold>
            <Panel className="grid gap-[12px] p-[14px]">
                <div className="grid gap-[12px] sm:grid-cols-2">
                    <Field
                        label="Directory (tenant) ID"
                        hint={
                            tenantId.trim() && !tenantValid
                                ? "Paste the GUID from the app's Overview page, or a domain."
                                : "On the app registration's Overview page."
                        }
                    >
                        <input
                            type="text"
                            value={tenantId}
                            spellCheck={false}
                            onChange={(event) => setTenantId(event.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className={`${inputCls} font-mono`}
                        />
                    </Field>
                    <ClientIdField
                        value={clientId}
                        onChange={setClientId}
                        hint={clientId.trim() && !clientValid ? "Paste the GUID from the app's Overview page." : "On the same Overview page."}
                    />
                </div>
                <ClientSecretField value={clientSecret} onChange={setClientSecret} />
                <div>
                    <button type="button" onClick={connect} disabled={!canConnect} className={primaryBtnCls}>
                        {saving ? "Checking with Microsoft..." : "Connect"}
                    </button>
                </div>
                <ErrorText>{error}</ErrorText>
            </Panel>
        </div>
    );
}

function EditConnectionForm({ integration, onSaved, onCancel, onClientId }) {
    const caps = useMyPermissions();
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const clientInvalid = clientId.trim().length > 0 && !GUID.test(clientId.trim());
    const canSave = !clientInvalid && clientSecret.trim().length > 0 && !saving;

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await replaceMicrosoftFoundrySecret(integration.id, {
                clientId: clientId.trim(),
                clientSecret: clientSecret.trim(),
            });
            if (clientId.trim()) onClientId(integration.id, clientId.trim());
            setClientSecret("");
            await onSaved();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, clientId, clientSecret, integration.id, onClientId, onSaved]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            <ClientIdField
                value={clientId}
                onChange={setClientId}
                hint={clientInvalid ? "Paste the GUID from the app's Overview page." : "Leave blank to keep the current one."}
            />
            <ClientSecretField value={clientSecret} onChange={setClientSecret} />
            <Note>Origin signs in with the new secret and checks every workspace before saving it.</Note>
            <div className="flex flex-wrap items-center gap-[8px]">
                <button type="button" onClick={save} disabled={!canSave} className={primaryBtnCls}>
                    {saving ? "Checking with Microsoft..." : "Save connection"}
                </button>
                <button type="button" onClick={onCancel} className={secondaryBtnCls}>
                    Cancel
                </button>
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

function RoleSteps({ managed, clientId }) {
    return (
        <div className="grid gap-[10px]">
            {managed ? (
                <Note>
                    Consent let Origin's app sign in to your tenant; this role lets it read a workspace, and consent never
                    grants it.
                </Note>
            ) : null}
            <Instructions>
                <li>
                    Azure portal: search <Ui>Log Analytics workspaces</Ui> and open the workspace.
                </li>
                <li>
                    <Ui>Access control (IAM)</Ui> &gt; <Ui>Add</Ui> &gt; <Ui>Add role assignment</Ui>.
                </li>
                <li>
                    <Ui>Role</Ui> tab: search <Ui>{ROLE}</Ui>, select it, and choose <Ui>Next</Ui>.
                </li>
                <li>
                    <Ui>Members</Ui> tab: <Ui>User, group, or service principal</Ui> &gt; <Ui>Select members</Ui>, then
                    search for {managed ? "Origin's app, the enterprise application your consent created" : "your app registration"}.
                </li>
                <li>
                    <Ui>Review + assign</Ui>.
                </li>
            </Instructions>
            {managed && clientId ? (
                <div className="grid gap-[6px]">
                    <Note>Its client ID, to check you picked the right app:</Note>
                    <CopyValue label="Origin's app client ID" value={clientId} />
                </div>
            ) : null}
        </div>
    );
}

function WorkspaceRequirements() {
    return (
        <div className="grid gap-[8px]">
            <Note>
                No workspace yet? Create one: Azure portal &gt; <Ui>Log Analytics workspaces</Ui> &gt; <Ui>Create</Ui>.
                Send your gateway logs and agent traces to it. A new workspace can take up to two hours to get its first
                rows.
            </Note>
            <Fold summary="Workspace requirements">
                <ul className="m-0 grid list-disc gap-[6px] pl-[16px] text-[12px] leading-[1.5] text-[color:var(--muted)]">
                    <li>
                        Allow public queries. A workspace that accepts queries only through a private link blocks Origin;
                        check its network isolation settings.
                    </li>
                    <li>
                        Keep <Mono>ApiManagementGatewayLlmLog</Mono> and <Mono>AppGenAIContent</Mono> on the Analytics
                        plan, in the workspace's <Ui>Tables</Ui>. Origin is not yet verified on the Basic plan.
                    </li>
                </ul>
            </Fold>
        </div>
    );
}

function AddWorkspaces({ integration, clientId, onChanged, onCancel }) {
    const caps = useMyPermissions();
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const ids = idList(text);
    const invalid = ids.filter((id) => !WORKSPACE_ID.test(id));
    const known = new Set(integration.workspaces.map((workspace) => workspace.workspaceResourceId));
    const fresh = ids.filter((id) => !known.has(id.toLowerCase()));
    const room = MAX_WORKSPACES - integration.workspaces.length;
    const tooMany = fresh.length > room;
    const canAdd = fresh.length > 0 && invalid.length === 0 && !tooMany && !saving;
    const valid = ids.filter((id) => WORKSPACE_ID.test(id));
    const command = roleCommand(clientId, valid);

    const add = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await addMicrosoftFoundryWorkspaces(integration.id, fresh);
            setText("");
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, fresh, integration.id, onChanged]);

    return (
        <div className="grid gap-[14px]">
            <Note>
                {foundryAppName(integration.managed, { sentenceStart: true })} needs one role on each workspace that
                receives your gateway logs or agent traces: <Ui>{ROLE}</Ui>. It is read-only; "Privileged" means it can
                also read protected tables. Assign it on the workspace itself, not on the Application Insights resource.
            </Note>
            <WorkspaceRequirements />
            <RoleSteps managed={integration.managed} clientId={clientId} />
            <Field
                label="Workspace resource IDs"
                error={
                    invalid.length > 0
                        ? `${invalid.length === 1 ? "This is not" : "These are not"} a Log Analytics workspace resource ID: ${invalid.join(", ")}`
                        : tooMany
                          ? `A connection reads at most ${MAX_WORKSPACES} workspaces; this one has room for ${room}.`
                          : null
                }
                hint="On the workspace's Overview page, under JSON View, or Properties > Resource ID. One per line."
            >
                <textarea
                    value={text}
                    spellCheck={false}
                    rows={2}
                    onChange={(event) => setText(event.target.value)}
                    placeholder="/subscriptions/.../resourceGroups/.../providers/Microsoft.OperationalInsights/workspaces/..."
                    className={`${inputCls} py-[8px] font-mono`}
                />
            </Field>
            <TerminalCommands>
                <CopyBlock
                    label="Grant the role from a terminal"
                    value={command}
                    highlight={clientId ? (valid.length > 0 ? null : WORKSPACE_RESOURCE_ID) : CLIENT_ID}
                />
                {clientId ? null : (
                    <Note>
                        Replace <Mono>{CLIENT_ID}</Mono> with the Application (client) ID of{" "}
                        {integration.managed
                            ? "Origin's app: Entra admin center > Enterprise applications, then Origin's app."
                            : "your app registration, from its Overview page."}
                    </Note>
                )}
            </TerminalCommands>
            <Note>The role takes a few minutes to apply. Add the workspace once it has.</Note>
            <div className="flex flex-wrap items-center gap-[8px]">
                <button type="button" onClick={add} disabled={!canAdd} className={primaryBtnCls}>
                    {saving ? "Checking the workspace..." : fresh.length > 1 ? `Add ${fresh.length} workspaces` : "Add workspace"}
                </button>
                {onCancel ? (
                    <button type="button" onClick={onCancel} className={secondaryBtnCls}>
                        Cancel
                    </button>
                ) : null}
            </div>
            <ErrorText>{error}</ErrorText>
        </div>
    );
}

function GatewayGuide({ workspaceId }) {
    return (
        <div className="grid gap-[12px]">
            <Note>
                Works on every API Management tier except Consumption. Consumption and self-hosted gateways write no
                logs.
            </Note>
            <Note>
                Only calls through an API Management gateway with message logging on reach Origin. Two settings: one on
                the instance, one on each API. An AI gateway created in Foundry is an API Management instance too: set it
                up the same way.
            </Note>
            <span className="text-[12px] font-medium text-[color:var(--ink)]">On the API Management instance</span>
            <Instructions>
                <li>
                    <Ui>Monitoring</Ui> &gt; <Ui>Diagnostic settings</Ui> &gt; <Ui>Add diagnostic setting</Ui>.
                </li>
                <li>
                    Tick <Ui>Logs related to ApiManagement Gateway</Ui> and <Ui>Logs related to generative AI gateway</Ui>.
                </li>
                <li>
                    <Ui>Send to Log Analytics workspace</Ui>: the workspace from step 3, with <Ui>Resource specific</Ui>{" "}
                    destination tables. Save.
                </li>
            </Instructions>
            <span className="text-[12px] font-medium text-[color:var(--ink)]">On each API that fronts a model</span>
            <Instructions>
                <li>
                    <Ui>APIs</Ui> &gt; the API &gt; <Ui>Settings</Ui> &gt; <Ui>Diagnostic Logs</Ui> &gt;{" "}
                    <Ui>Azure Monitor</Ui>.
                </li>
                <li>
                    <Ui>Log LLM messages</Ui>: Enabled. <Ui>Log prompts</Ui> and <Ui>Log completions</Ui>: 262144 bytes
                    each, the most the API allows. That keeps 131,072 characters of each prompt; a smaller number cuts
                    long prompts.
                </li>
                <li>
                    <Ui>Sampling</Ui>: 100 percent. Anything lower logs a sample, with nothing in the rows to say so.
                </li>
            </Instructions>
            <TerminalCommands>
                <CopyBlock label="The same from a terminal" value={gatewayCommands(workspaceId)} highlight="APIM_NAME" />
                <Note>
                    Replace <Mono>SUBSCRIPTION_ID</Mono>, <Mono>RESOURCE_GROUP</Mono> and <Mono>APIM_NAME</Mono> with the
                    instance's, and <Mono>API_ID</Mono> with the API's name. The last command shows what was saved:
                    sampling at 100, logs enabled, and 262144 for both requests and responses.
                </Note>
            </TerminalCommands>
            <Note>
                Each logged prompt and answer is billed as workspace ingestion. The first rows arrive minutes after a
                call; a new diagnostic setting can take up to 90 minutes to start.
            </Note>
        </div>
    );
}

function TracingGuide() {
    return (
        <div className="grid gap-[12px]">
            <Instructions>
                <li>
                    At <Ui>ai.azure.com</Ui>, with the New Foundry toggle on, open the project.
                </li>
                <li>
                    <Ui>Agents</Ui> &gt; <Ui>Traces</Ui> &gt; <Ui>Connect</Ui>.
                </li>
                <li>
                    Create an Application Insights resource on the workspace from step 3, or connect an existing one that
                    writes to it.
                </li>
            </Instructions>
            <Note>
                From then on every agent in the project is traced, its prompts and answers included. Repeat for each
                project: a project with no Application Insights connection is invisible to Origin.
            </Note>
            <Note>
                To check an existing Application Insights resource, open it in the Azure portal: its <Ui>Overview</Ui>{" "}
                shows the workspace it writes to, which must be the one from step 3.
            </Note>
            <TerminalCommands>
                <CopyBlock
                    label="Check where an existing Application Insights resource writes"
                    value={`az resource show --ids ${APP_INSIGHTS_RESOURCE_ID} --query properties.WorkspaceResourceId --output tsv`}
                    highlight={APP_INSIGHTS_RESOURCE_ID}
                />
                <Note>It must print the resource ID of the workspace from step 3.</Note>
            </TerminalCommands>
        </div>
    );
}

function HardeningGuide() {
    return (
        <div className="grid gap-[8px]">
            <Note>
                Optional. An app that calls a model deployment directly, with no gateway and no tracing, leaves no prompt
                text Origin can read. To send every call through the gateway, disable key authentication on the Foundry
                resource and restrict its network access to the gateway.
            </Note>
        </div>
    );
}

// None of this setup needs Origin, so it shows before a connection exists too.
function SourceGuides({ workspaceId = "" }) {
    const [guide, setGuide] = useState(null);
    const toggle = (name) => () => setGuide((open) => (open === name ? null : name));
    return (
        <div className="grid gap-[10px]">
            <div className="flex flex-wrap gap-[8px]">
                <GuideToggle open={guide === "gateway"} onToggle={toggle("gateway")} label="gateway logging" />
                <GuideToggle open={guide === "tracing"} onToggle={toggle("tracing")} label="agent tracing" />
                <GuideToggle
                    open={guide === "hardening"}
                    onToggle={toggle("hardening")}
                    text="Optional: calls that skip the gateway"
                />
            </div>
            {guide === "gateway" ? <GatewayGuide workspaceId={workspaceId} /> : null}
            {guide === "tracing" ? <TracingGuide /> : null}
            {guide === "hardening" ? <HardeningGuide /> : null}
        </div>
    );
}

// Shown once a workspace has had 90 minutes, the longest a new diagnostic setting takes to start,
// and Origin has read nothing from it.
const NO_DATA_AFTER_MS = 90 * 60 * 1000;

function stillWaiting(workspace, now) {
    if (workspace.tables.some((table) => table.readThrough) || !workspace.addedAt) return false;
    return now - Date.parse(workspace.addedAt) >= NO_DATA_AFTER_MS;
}

function NoDataChecklist({ managed }) {
    return (
        <Panel className="grid gap-[10px] p-[14px]">
            <span className="text-[12px] font-medium text-[color:var(--ink)]">Still no data? Check, in order:</span>
            <Instructions>
                <li>The role is on the workspace itself, for {foundryAppName(managed)}.</li>
                <li>
                    The API Management diagnostic setting includes <Ui>Logs related to ApiManagement Gateway</Ui>, not
                    only the generative AI gateway logs. Without it, no gateway call can be captured.
                </li>
                <li>
                    Each API has <Ui>Log LLM messages</Ui> on and <Ui>Sampling</Ui> at 100 percent.
                </li>
                <li>Apps call the gateway's URL, not the model's own endpoint.</li>
            </Instructions>
            <Note>
                Checking the workspace yourself? On a workspace set to <Ui>Data actions only</Ui>, or for a protected
                table, your account needs the same role. Without it your queries return no rows, even as Owner.
            </Note>
        </Panel>
    );
}

function WorkspaceRow({ workspace, managed, onRemove }) {
    const problems = [
        workspace.status === "error" ? workspace.lastSyncError || "The last read of this workspace failed." : null,
        workspace.missingRole ? foundryMissingRole(managed) : null,
        workspace.gatewayLogsMissing ? FOUNDRY_GATEWAY_LOGS_MISSING : null,
    ].filter(Boolean);
    const lastPoll = whenLabel(workspace.lastPollAt);
    const lastRead = whenLabel(workspace.lastSuccessAt);
    return (
        <Panel className="grid gap-[10px] p-[14px]">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-[10px]">
                <div className="grid min-w-0 gap-[2px]">
                    <span className="font-mono text-[12.5px] text-[color:var(--ink)]">{resourceName(workspace.workspaceResourceId)}</span>
                    <span title={workspace.workspaceResourceId} className="truncate font-mono text-[11px] text-[color:var(--muted)]">
                        {workspace.workspaceResourceId}
                    </span>
                </div>
                <div className="flex items-center gap-[8px]">
                    <span className={`text-[12px] ${problems.length > 0 ? "text-[color:var(--severity-high)]" : "text-[color:var(--muted)]"}`}>
                        {problems.length > 0 && workspace.status !== "error" ? "Needs attention" : WORKSPACE_STATUS[workspace.status] ?? workspace.status}
                    </span>
                    <button type="button" onClick={() => onRemove(workspace)} className={secondaryBtnCls}>
                        Stop reading
                    </button>
                </div>
            </div>
            <dl className="m-0 grid grid-cols-2 gap-x-[16px] gap-y-[4px] text-[12px] leading-[1.45] sm:grid-cols-4">
                <dt className="text-[color:var(--muted)]">Last poll</dt>
                <dd className="m-0 text-[color:var(--ink)]">{lastPoll || "Not yet"}</dd>
                <dt className="text-[color:var(--muted)]">Last read</dt>
                <dd className="m-0 text-[color:var(--ink)]">{lastRead || "Not yet"}</dd>
                {workspace.tables.map((table) => (
                    <TableFact key={table.name} table={table} />
                ))}
            </dl>
            {problems.map((problem) => (
                <p key={problem} role="alert" className="m-0 text-[12px] leading-[1.45] text-[color:var(--severity-high)]">
                    {problem}
                </p>
            ))}
        </Panel>
    );
}

function TableFact({ table }) {
    const through = whenLabel(table.readThrough);
    return (
        <>
            <dt className="text-[color:var(--muted)]">{TABLE_LABEL[table.name] ?? table.name}</dt>
            <dd className="m-0 text-[color:var(--ink)]">
                {table.state === "no_table_yet" ? "Not in this workspace" : through ? `Read through ${through}` : "None read yet"}
            </dd>
        </>
    );
}

// Every capture cycle waits for a connected directory, so the admin hears it before the Azure work.
function DirectoryFirst({ onOpenIdentity }) {
    return (
        <div className="grid justify-items-start gap-[6px] border-l-[3px] border-[color:var(--severity-medium)] bg-[color:var(--panel)] px-[14px] py-[10px] text-[12px] leading-[1.5] text-[color:var(--ink)]">
            <p className="m-0">
                <Ui>Connect a directory first.</Ui> Origin captures Foundry calls only after an Entra, Okta or Google
                directory is connected. Calls aren't tied to people, but capture stays off until one is.
            </p>
            {onOpenIdentity ? (
                <button type="button" onClick={onOpenIdentity} className={`${linkCls} w-fit`}>
                    Identity connections
                </button>
            ) : null}
        </div>
    );
}

// Step 3's one-line summary, naming the app the admin gives the role to once the method is known.
function readsWorkspaces(managed) {
    if (managed === true) return "Origin's app reads each Log Analytics workspace you grant it a role on.";
    if (managed === false) return "Origin reads each Log Analytics workspace you grant your app registration a role on.";
    return "Origin reads each Log Analytics workspace you grant the app a role on.";
}

function ConnectSteps({ managedSignIn, recommendManaged, onCreated, onClientId }) {
    const [method, setMethod] = useState(null);
    const chosenManaged = method === CONNECT_METHOD.MANAGED ? true : method === CONNECT_METHOD.BYO ? false : null;

    return (
        <Steps label="Connect Microsoft Foundry">
            <Step number={1} state={method ? "done" : "current"} title="Choose how Origin connects to Microsoft">
                <Panel className="grid gap-0">
                    <MethodOption
                        name="foundry-connect-method"
                        selected={method === CONNECT_METHOD.MANAGED}
                        onSelect={() => setMethod(CONNECT_METHOD.MANAGED)}
                        title={MICROSOFT_CONNECT_METHODS.MANAGED.title}
                        description={MICROSOFT_CONNECT_METHODS.MANAGED.description}
                        disabledReason={managedSignIn ? null : MANAGED_SIGN_IN_UNAVAILABLE}
                        recommended={managedSignIn && recommendManaged && MICROSOFT_CONNECT_METHODS.MANAGED.recommended}
                    />
                    <MethodOption
                        name="foundry-connect-method"
                        selected={method === CONNECT_METHOD.BYO}
                        onSelect={() => setMethod(CONNECT_METHOD.BYO)}
                        title={MICROSOFT_CONNECT_METHODS.BRING_YOUR_OWN.title}
                        description="You register an app in Entra and rotate its client secret. It needs no API permissions, only the workspace role."
                    />
                </Panel>
            </Step>
            <Step
                number={2}
                state={method ? "current" : "todo"}
                title={method === CONNECT_METHOD.BYO || !managedSignIn ? "Paste the app registration" : "Grant admin consent"}
                summary={method ? null : "Starts after you choose how to connect."}
            >
                {method === CONNECT_METHOD.MANAGED ? (
                    <ManagedConnectForm onCreated={onCreated} />
                ) : method === CONNECT_METHOD.BYO ? (
                    <ByoConnectForm onCreated={onCreated} onClientId={onClientId} />
                ) : null}
            </Step>
            <Step
                number={3}
                state="todo"
                title="Grant the role and add a workspace"
                summary={readsWorkspaces(chosenManaged)}
            >
                <WorkspaceRequirements />
            </Step>
            <Step
                number={4}
                state="todo"
                title="Send gateway logs or agent traces to the workspace"
                summary="Gateway calls with message logging on, traced Foundry agents, or both. This can be set up now, before connecting."
            >
                <SourceGuides />
            </Step>
            <Step number={5} state="todo" title="Capture starts" summary="Origin reads each workspace about once a minute." last />
        </Steps>
    );
}

function ConnectionSteps({ integration, directory, clientId, onClientId, onChanged, onRemoveWorkspace, refreshing }) {
    const caps = useMyPermissions();
    const [, consentState, workspaceState, sourcesState, readState] = foundrySteps(integration);
    const noDirectory = readState === "done" && directoryMissing(directory);
    const captureState = noDirectory ? "failed" : readState;
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [editing, setEditing] = useState(false);
    const [adding, setAdding] = useState(false);
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;
    const { pendingId, start, cancel } = useDirectoryOauthPopup({
        onResult: ({ status, message }) => {
            if (status === "success") onChangedRef.current();
            else setError(message || "Microsoft did not complete admin consent.");
        },
        onClosedWithoutResult: () => onChangedRef.current(),
    });
    const pending = integration.status === "pending";
    const tenant = integration.entraTenantId;
    const workspaces = integration.workspaces;
    const readTables = [
        ...new Set(
            workspaces.flatMap((workspace) =>
                workspace.tables.filter((table) => table.readThrough).map((table) => TABLE_LABEL[table.name] ?? table.name),
            ),
        ),
    ];
    const lastRead = whenLabel(integration.lastSuccessAt);

    const continueConsent = useCallback(async () => {
        setStarting(true);
        setError(null);
        try {
            const redirectUri = directoryOauthRedirectUri(window.location.origin);
            const { authUrl } = await getMicrosoftFoundryAuthUrl({ integrationId: integration.id, redirectUri });
            if (!start({ integrationId: integration.id, provider: CONSENT_PROVIDER, redirectUri, authUrl })) {
                setError(POPUP_BLOCKED);
            }
        } catch (err) {
            cancel();
            setError(denialOrRawMessage(err, caps));
        } finally {
            setStarting(false);
        }
    }, [cancel, caps, integration.id, start]);

    const saved = useCallback(async () => {
        setEditing(false);
        await onChanged();
    }, [onChanged]);

    const added = useCallback(async () => {
        setAdding(false);
        await onChanged();
    }, [onChanged]);

    return (
        <Steps label={`Microsoft Foundry connection ${foundryConnectionName(integration) || "waiting for consent"}`}>
            <Step
                number={1}
                state={editing ? "current" : "done"}
                title={(integration.managed ? MICROSOFT_CONNECT_METHODS.MANAGED : MICROSOFT_CONNECT_METHODS.BRING_YOUR_OWN).title}
                summary={
                    tenant ? (
                        <>
                            Microsoft tenant <Mono>{tenant}</Mono>
                            {integration.organizationName ? `, ${integration.organizationName}` : ""}.
                        </>
                    ) : (
                        "The Microsoft tenant is set when an admin grants consent."
                    )
                }
                action={
                    !integration.managed && !editing ? (
                        <button type="button" onClick={() => setEditing(true)} className={secondaryBtnCls}>
                            Edit connection
                        </button>
                    ) : null
                }
            >
                {editing ? (
                    <EditConnectionForm
                        integration={integration}
                        onSaved={saved}
                        onCancel={() => setEditing(false)}
                        onClientId={onClientId}
                    />
                ) : null}
            </Step>
            <Step
                number={2}
                state={consentState}
                title={integration.managed ? "Grant admin consent" : "Verify the app registration"}
                summary={
                    consentState === "done"
                        ? integration.managed
                            ? "Admin consent granted."
                            : "Microsoft accepted the app registration."
                        : consentState === "failed"
                          ? foundryProblem(integration)
                          : "Waiting for a Microsoft admin to grant consent. Nothing is captured until then."
                }
                action={
                    pending && integration.managed && !pendingId ? (
                        <button type="button" onClick={continueConsent} disabled={starting} className={primaryBtnCls}>
                            {starting ? "Opening Microsoft..." : "Continue to Microsoft"}
                        </button>
                    ) : null
                }
            >
                {pending && integration.managed ? <ManagedPermissionList /> : null}
                {pending && integration.managed ? <ConsentScreens /> : null}
                {pendingId ? <ConsentWait onCancel={cancel} message={CONSENT_WAIT} /> : null}
                <ErrorText>{error}</ErrorText>
            </Step>
            <Step
                number={3}
                state={workspaceState}
                title="Grant the role and add a workspace"
                summary={
                    pending
                        ? "Starts after admin consent: Origin's app exists in your tenant only once it is granted."
                        : workspaces.length === 0
                          ? readsWorkspaces(integration.managed)
                          : `Reading ${workspaces.length === 1 ? "1 workspace" : `${workspaces.length} workspaces`}, up to ${MAX_WORKSPACES}.`
                }
                action={
                    workspaces.length > 0 && !adding ? (
                        <button type="button" onClick={() => setAdding(true)} className={secondaryBtnCls}>
                            Add workspace
                        </button>
                    ) : null
                }
            >
                {pending || (workspaces.length > 0 && !adding) ? null : (
                    <AddWorkspaces
                        integration={integration}
                        clientId={clientId}
                        onChanged={added}
                        onCancel={workspaces.length > 0 ? () => setAdding(false) : null}
                    />
                )}
            </Step>
            <Step
                number={4}
                state={sourcesState}
                title="Send gateway logs or agent traces to the workspace"
                summary={
                    readTables.length > 0
                        ? `Origin has read ${readTables.join(" and ").toLowerCase()}.`
                        : "Turn on gateway message logging, agent tracing, or both. Either is enough."
                }
            >
                <SourceGuides workspaceId={workspaces[0]?.workspaceResourceId ?? ""} />
            </Step>
            <Step
                number={5}
                state={captureState}
                title={
                    noDirectory
                        ? NO_DIRECTORY.label
                        : captureState === "done"
                          ? "Capturing"
                          : captureState === "failed"
                            ? "Capture needs attention"
                            : "Capture starts"
                }
                summary={
                    noDirectory
                        ? NO_DIRECTORY.detail
                        : captureState === "failed"
                          ? foundryProblem(integration)
                          : captureState === "done"
                            ? `Last read ${lastRead || "not yet"}.`
                            : workspaces.length > 0
                              ? "No data has reached the workspace yet. A new diagnostic setting can take up to 90 minutes to write its first row."
                              : "Origin reads each workspace about once a minute."
                }
                action={
                    workspaces.length > 0 && captureState !== "done" ? (
                        <button type="button" onClick={onChanged} disabled={refreshing} className={secondaryBtnCls}>
                            {refreshing ? "Checking..." : "Refresh"}
                        </button>
                    ) : null
                }
                last
            >
                {workspaces.length > 0 ? (
                    <div className="grid gap-[10px]">
                        {workspaces.some((workspace) => stillWaiting(workspace, Date.now())) ? (
                            <NoDataChecklist managed={integration.managed} />
                        ) : null}
                        {workspaces.map((workspace) => (
                            <WorkspaceRow
                                key={workspace.workspaceResourceId}
                                workspace={workspace}
                                managed={integration.managed}
                                onRemove={onRemoveWorkspace}
                            />
                        ))}
                    </div>
                ) : null}
            </Step>
        </Steps>
    );
}

export default function MicrosoftFoundryPage({
    source,
    health,
    entry,
    directory,
    onBack,
    onChanged,
    selectedItem,
    onSelectItem,
    onOpenIdentity,
}) {
    const caps = useMyPermissions();
    const notOffered = entry?.data === FOUNDRY_NOT_OFFERED;
    const integrations = notOffered ? null : (entry?.data?.integrations ?? null);
    const managedSignIn = entry?.data?.managedSignIn !== false;
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [confirmWorkspace, setConfirmWorkspace] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState(null);
    const knownIds = useRef(null);
    // List names Origin's managed app. A bring-your-own client ID is write-only on the server,
    // so the role command can name it only while this page remembers what the admin typed.
    const [clientIds, setClientIds] = useState({});
    const rememberClientId = useCallback((id, clientId) => setClientIds((known) => ({ ...known, [id]: clientId })), []);

    const selected =
        integrations && integrations.some((integration) => integration.id === selectedItem)
            ? selectedItem
            : integrations && integrations.length > 0 && selectedItem !== NEW_TAB
              ? integrations[0].id
              : NEW_TAB;
    const current = integrations?.find((integration) => integration.id === selected) ?? null;

    const created = useCallback(async () => {
        knownIds.current = new Set((integrations ?? []).map((integration) => integration.id));
        await onChanged();
    }, [integrations, onChanged]);

    useEffect(() => {
        if (!knownIds.current || !integrations) return;
        const added = integrations.find((integration) => !knownIds.current.has(integration.id));
        knownIds.current = null;
        if (added) onSelectItem(added.id);
    }, [integrations, onSelectItem]);

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setRemoving(true);
        setError(null);
        try {
            await deleteMicrosoftFoundryIntegration(target.id);
            onSelectItem(null);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmRemove, onChanged, onSelectItem]);

    const removeWorkspace = useCallback(async () => {
        const target = confirmWorkspace;
        setConfirmWorkspace(null);
        if (!target || !current) return;
        setRemoving(true);
        setError(null);
        try {
            await removeMicrosoftFoundryWorkspace(current.id, target.workspaceResourceId);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmWorkspace, current, onChanged]);

    if (notOffered) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={entry.loading} aside={null}>
                <div className="grid justify-items-start gap-[10px]">
                    <Note>This Origin environment does not offer Microsoft Foundry yet.</Note>
                    <button type="button" onClick={onChanged} disabled={entry.loading} className={secondaryBtnCls}>
                        {entry.loading ? "Checking..." : "Check again"}
                    </button>
                </div>
            </SourcePage>
        );
    }

    if (!integrations) {
        return (
            <SourcePage
                source={source}
                health={health}
                onBack={onBack}
                backLabel="Back to data sources"
                busy={!entry?.loaded || entry?.loading}
                aside={null}
            >
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const tabs =
        integrations.length > 0 ? (
            <Tabs
                label="Microsoft tenants"
                selected={selected}
                onSelect={onSelectItem}
                items={[
                    ...integrations.map((integration) => ({
                        id: integration.id,
                        label: foundryConnectionName(integration) || "Waiting for consent",
                        mono: !integration.organizationName && Boolean(integration.entraTenantId),
                        dotTone: connectionTone(integration),
                    })),
                    { id: NEW_TAB, label: "+ Connect Microsoft tenant" },
                ]}
            />
        ) : null;

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            tabs={tabs}
            aside={
                <>
                    <AsideSection title="What Origin does in your Azure">
                        <p className="m-0">
                            Reads the gateway and agent-trace tables of the workspaces you add. Changes nothing in your
                            Azure.
                        </p>
                    </AsideSection>
                    <AsideFold title="Who is captured">
                        <p className="m-0">
                            Each prompt is attributed to the API Management subscription key, the API, or the Foundry agent
                            that made it, never to a person: Azure logs no one's identity.
                        </p>
                        {directoryScoped(directory) ? (
                            <p className="m-0">
                                Your directory syncs only selected groups, but Foundry still captures every call, because
                                calls belong to keys, APIs and agents, not people.
                            </p>
                        ) : null}
                        <p className="m-0">
                            When you add a workspace, Origin also reads its last day of calls. Older calls are not
                            captured.
                        </p>
                        <p className="m-0">
                            People whose Origin access is limited to directory groups don't see Foundry prompts: keys,
                            APIs and agents belong to no group.
                        </p>
                        {onOpenIdentity ? (
                            <button type="button" onClick={onOpenIdentity} className={`${linkCls} w-fit`}>
                                Identity connections
                            </button>
                        ) : null}
                    </AsideFold>
                    <AsideFold title="Claude models on the gateway (preview)">
                        <p className="m-0">
                            Capturing Claude calls on the gateway's Anthropic route is in preview. If Origin can't read one
                            of these calls, it skips that call and keeps capturing the rest.
                        </p>
                    </AsideFold>
                    {current ? null : (
                        <AsideFold title="Roles each step needs">
                            <ul className="m-0 grid list-disc gap-[4px] pl-[16px]">
                                <li>
                                    Connect: Global Administrator or Privileged Role Administrator to sign in with Microsoft,
                                    or permission to register an app in Entra to use your own.
                                </li>
                                <li>Grant the role: Owner or User Access Administrator on the workspace.</li>
                                <li>Gateway logging: Contributor on the API Management instance.</li>
                                <li>Agent tracing: owner of the Foundry project.</li>
                            </ul>
                        </AsideFold>
                    )}
                    {current ? (
                        <AsideSection title="Disconnect">
                            <p className="m-0">
                                Capture stops for this Microsoft tenant. Prompts already captured are kept.
                            </p>
                            <button
                                type="button"
                                onClick={() => setConfirmRemove(current)}
                                disabled={removing}
                                className={`${dangerBtnCls} w-fit`}
                            >
                                Disconnect
                            </button>
                        </AsideSection>
                    ) : null}
                    <ErrorText>{error}</ErrorText>
                </>
            }
        >
            <div className="grid gap-[18px]">
                {directoryMissing(directory) ? <DirectoryFirst onOpenIdentity={onOpenIdentity} /> : null}
                {current ? (
                    <ConnectionSteps
                        key={current.id}
                        integration={current}
                        directory={directory}
                        clientId={current.clientId || clientIds[current.id] || ""}
                        onClientId={rememberClientId}
                        onChanged={onChanged}
                        onRemoveWorkspace={setConfirmWorkspace}
                        refreshing={entry.loading}
                    />
                ) : (
                    <ConnectSteps
                        managedSignIn={managedSignIn}
                        recommendManaged={consentedThroughOriginsApp(directory)}
                        onCreated={created}
                        onClientId={rememberClientId}
                    />
                )}
            </div>
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.FOUNDRY_DISCONNECT}
                eyebrow="Microsoft Foundry"
                title="Disconnect this Microsoft tenant?"
                body="Capture stops for this Microsoft tenant and its workspaces. Captured prompts are kept. The role assignments and the consent stay in your Azure; remove them there."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
            <ConfirmDialog
                open={Boolean(confirmWorkspace)}
                analyticsId={DIALOG.FOUNDRY_WORKSPACE_REMOVE}
                eyebrow="Microsoft Foundry"
                title="Stop reading this workspace?"
                body="Origin stops reading it and forgets how far it read. Adding it again reads back one day. Captured prompts are kept."
                confirmLabel="Stop reading"
                destructive
                onConfirm={removeWorkspace}
                onCancel={() => setConfirmWorkspace(null)}
            />
        </SourcePage>
    );
}
